import styles from "./edit-event.module.scss";
import baseStyles from "../styles/App.module.scss";
import classNames from "classnames";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { InputTextarea } from "primereact/inputtextarea";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useState, useRef, useEffect } from "react";
import {
    FormikErrors,
    FormikHandlers,
    FormikTouched,
    FormikValues,
} from "formik/dist/types";
import { EVENT_DISPLAYNAME_MAXLENGTH, HOSTNAME_MAXLENGTH } from "../constants";
import { Location } from "../dtos/location";
import { Event } from "../dtos/event";
import Autocomplete from "react-google-autocomplete";
import EventLocation from "../shared/event-location";
import { checkEditKey, getEvent, updateEvent } from "../api";
import { useNavigate, useParams } from "react-router-dom";
import ImageUpload, { ImageUploadRef } from "../image-upload/image-upload";

interface FormValues {
    displayName: string;
    startDate: Date;
    startTime: Date;
    endDate?: Date;
    endTime?: Date;
    hostName: string;
    location?: Location;
    description: string;
    imageId: string;
}

const renderStep = (
    step: number,
    values: FormValues,
    errors: FormikErrors<FormValues>,
    touched: FormikTouched<FormValues>,
    handleChange: FormikHandlers["handleChange"],
    handleBlur: FormikHandlers["handleBlur"],
    setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean | undefined
    ) => void,
    imageUploadRef: React.RefObject<ImageUploadRef>
) => {
    switch (step) {
        case 1:
            return (
                <BasicDataForm
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                />
            );
        case 2:
            return <LocationForm setFieldValue={setFieldValue} values={values} />;
        case 3:
            return <ImageForm
                values={values}
                handleChange={handleChange}
                handleBlur={handleBlur}
                imageUploadRef={imageUploadRef}
            />;
        case 4:
            return (
                <DescriptionForm
                    values={values}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                />
            );
        default:
            return <div>default</div>;
    }
};

const defaultLocation: Location = {
    address: null,
    lon: null,
    lat: null,
    name: null,
};

function EditEvent() {
    const [step, setStep] = useState(0); // Start at 0 for edit key input
    const [editKey, setEditKey] = useState("");
    const [eventData, setEventData] = useState<Event | null>(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isLoadingEvent, setIsLoadingEvent] = useState(false);
    const imageUploadRef = useRef<ImageUploadRef>(null);
    const navigate = useNavigate();
    const { eventId } = useParams<{ eventId: string }>();

    // Function to load event data
    const loadEventData = async () => {
        if (!eventId) {
            setErrorMessage("No event ID provided");
            return;
        }

        setIsLoadingEvent(true);
        setErrorMessage(null);

        try {
            const event = await getEvent(eventId);
            setEventData(event);
            setStep(1); // Move to the first editing step
        } catch (error) {
            console.error("Error loading event:", error);
            setErrorMessage("Failed to load event. Please check the event ID and try again.");
            setStep(0); // Stay on edit key input
        } finally {
            setIsLoadingEvent(false);
        }
    };

    const handleEditKeySubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const isValid = await checkEditKey(eventId!, editKey);
        if (!isValid) {
            setErrorMessage("Invalid edit key");
            return;
        }
        loadEventData();
    };

    const validationSchema = Yup.object({
        displayName: Yup.string()
            .max(
                EVENT_DISPLAYNAME_MAXLENGTH,
                `Event Name cannot be longer than ${EVENT_DISPLAYNAME_MAXLENGTH} characters.`
            )
            .required("Event Name is required"),
        startDate: Yup.date().required("Start Date is required"),
        hostName: Yup.string()
            .max(
                HOSTNAME_MAXLENGTH,
                `Host Name cannot be longer than ${HOSTNAME_MAXLENGTH} characters.`
            )
            .required("Host Name is required"),
    });

    const handleNext = async () => {
        // If we're on step 3 (image form), crop and upload the image first
        if (step === 3 && imageUploadRef.current?.hasImage()) {
            setIsUploadingImage(true);
            try {
                await imageUploadRef.current.cropAndUpload();
                setStep(step + 1);
            } catch (error) {
                console.error('Error uploading image:', error);
                // Don't proceed to next step if upload fails
            } finally {
                setIsUploadingImage(false);
            }
        } else {
            setStep(step + 1);
        }
    };

    const handlePrevious = () => {
        if (step === 1) {
            // Go back to edit key input
            setStep(0);
            setEventData(null);
        } else {
            setStep(step - 1);
        }
    };

    const handleNavigation = (url: string) => {
        navigate(url);
    };

    const handleSubmit = async (values: FormikValues) => {
        if (!eventId || !editKey) {
            setErrorMessage("Missing event ID or edit key");
            return;
        }

        try {
            setErrorMessage(null);
            console.log(values);

            // Create a new date to avoid mutating the original
            let startDate = new Date(values.startDate);
            startDate.setHours(
                values.startTime.getHours(),
                values.startTime.getMinutes(),
                0,
                0
            );

            let endDate = null;
            if (values.endDate && values.endTime) {
                endDate = new Date(values.endDate);
                endDate.setHours(
                    values.endTime.getHours(),
                    values.endTime.getMinutes(),
                    0,
                    0
                );
            }

            // Format as ISO but without timezone conversion
            const formatAsUTCWithLocalTime = (date: Date): string => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                const seconds = String(date.getSeconds()).padStart(2, '0');

                return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`;
            };

            const updatedEventData: Event = {
                displayName: values.displayName,
                allday: false,
                hostName: values.hostName,
                startDate: formatAsUTCWithLocalTime(startDate),
                endDate: endDate ? formatAsUTCWithLocalTime(endDate) : null,
                location: values.location,
                description: values.description,
                imageId: values.imageId,
            };

            const updatedEvent = await updateEvent(eventId, updatedEventData, editKey);
            handleNavigation(`/e/${updatedEvent.id}`);
        } catch (error) {
            console.error("Error updating event:", error);
            setErrorMessage("❌ Failed to update event. Please check your edit key and try again.");
        }
    };

    const handleCancel = () => {
        if (eventId) {
            navigate(`/e/${eventId}`);
        } else {
            navigate('/');
        }
    };

    const handleUpdateEvent = async (
        validateForm: () => Promise<FormikErrors<FormValues>>,
        setTouched: (touched: FormikTouched<FormValues>) => void,
        submitForm: () => Promise<void>
    ) => {
        // Validate the form
        const errors = await validateForm();

        // Touch all fields to show errors
        setTouched({
            displayName: true,
            startDate: true,
            startTime: true,
            hostName: true,
            endDate: true,
            endTime: true,
            location: true,
            description: true,
            imageId: true,
        });

        // If there are errors, go back to step 1 to show them
        if (Object.keys(errors).length > 0) {
            setStep(1);
            setErrorMessage("Please fill in all required fields.");
            return;
        }

        // If validation passes, submit the form
        await submitForm();
    };

    // Function to check if current step fields pass validation
    const isCurrentStepValid = (
        step: number,
        values: FormValues,
        errors: FormikErrors<FormValues>
    ): boolean => {
        switch (step) {
            case 1:
                // Step 1 requires: displayName, startDate, hostName
                return (
                    !errors.displayName &&
                    !errors.startDate &&
                    !errors.hostName
                );
            case 2:
                // Step 2 has no required fields (location is optional)
                return true;
            case 3:
                // Step 3 has no required fields (image is optional)
                return true;
            case 4:
                // Step 4 has no required fields (description is optional)
                return true;
            default:
                return false;
        }
    };

    // Parse date from ISO string
    const parseDate = (dateString: string | null | undefined): Date => {
        if (!dateString) {
            return new Date();
        }
        return new Date(dateString);
    };

    // Render edit key input form
    if (step === 0) {
        return (
            <div className={classNames("grid grid-nogutter", styles.localMargin)}>
                <div
                    className={classNames(
                        "col-12 md:col-8 md:col-offset-2 lg:col-6 lg:col-offset-3 xl:col-4 xl:col-offset-4",
                        styles.topMargin
                    )}
                >
                    <div className={styles.editKeyInputContainer}>
                        <h2>Edit Event</h2>
                        <p>Please enter your edit key to modify this event.</p>

                        <form onSubmit={handleEditKeySubmit}>
                            <div className="p-fluid">
                                <span className="p-float-label">
                                    <InputText
                                        id="editKeyInput"
                                        type="text"
                                        value={editKey}
                                        onChange={(e) => setEditKey(e.target.value)}
                                        className={classNames({ "p-invalid": errorMessage })}
                                    />
                                    <label htmlFor="editKeyInput">Edit Key</label>
                                </span>
                            </div>

                            {errorMessage && (
                                <div className={classNames("mt-3", baseStyles.errorColor)}>
                                    {errorMessage}
                                </div>
                            )}

                            <div className={styles.buttonBar}>
                                <button
                                    type="button"
                                    className={classNames(
                                        baseStyles.btn,
                                        baseStyles.btnPrimary,
                                        baseStyles.btnMd,
                                        baseStyles.btnDanger,
                                        baseStyles.centerVer
                                    )}
                                    onClick={handleCancel}
                                >
                                    <span className="material-icons"> close </span>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className={classNames(
                                        baseStyles.btn,
                                        baseStyles.btnPrimary,
                                        baseStyles.btnMd,
                                        baseStyles.centerVer
                                    )}
                                    disabled={isLoadingEvent}
                                >
                                    {isLoadingEvent ? 'Loading...' : 'Continue'}
                                    {!isLoadingEvent && <span className="material-icons"> arrow_forward </span>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    // Don't render the form until we have event data
    if (!eventData) {
        return (
            <div className={classNames("grid grid-nogutter", styles.localMargin)}>
                <div
                    className={classNames(
                        "col-12 md:col-8 md:col-offset-2 lg:col-6 lg:col-offset-3 xl:col-4 xl:col-offset-4",
                        styles.topMargin
                    )}
                >
                    <p>Loading event data...</p>
                </div>
            </div>
        );
    }

    // Parse dates from event data
    const startDateTime = parseDate(eventData.startDate);
    const endDateTime = eventData.endDate ? parseDate(eventData.endDate) : undefined;

    const initialValues: FormValues = {
        displayName: eventData.displayName || "",
        startDate: startDateTime,
        startTime: startDateTime,
        endDate: endDateTime,
        endTime: endDateTime,
        location: eventData.location || {
            name: defaultLocation.name,
            placeId: "",
            address: defaultLocation.address,
            lon: defaultLocation.lon,
            lat: defaultLocation.lat,
        },
        description: eventData.description || "",
        imageId: eventData.imageId || "",
        hostName: eventData.hostName || "",
    };

    return (
        <div className={classNames("grid grid-nogutter", styles.localMargin)}>
            <div
                className={classNames(
                    "col-12 md:col-8 md:col-offset-2 lg:col-6 lg:col-offset-3 xl:col-4 xl:col-offset-4",
                    styles.topMargin
                )}
            >
                <Formik
                    initialValues={initialValues}
                    onSubmit={(values) => handleSubmit(values)}
                    validationSchema={validationSchema}
                    enableReinitialize={true}
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        setFieldValue,
                        isValid,
                        setTouched,
                        validateForm,
                        submitForm,
                    }) => (
                        <Form>
                            {renderStep(
                                step,
                                values,
                                errors,
                                touched,
                                handleChange,
                                handleBlur,
                                setFieldValue,
                                imageUploadRef
                            )}
                            {errorMessage && (
                                <div className={classNames("col-12 mt-3", baseStyles.errorColor)}>
                                    {errorMessage}
                                </div>
                            )}
                            <div className={styles.buttonBar}>
                                {step === 1 && (
                                    <button
                                        type="button"
                                        className={classNames(
                                            baseStyles.btn,
                                            baseStyles.btnPrimary,
                                            baseStyles.btnMd,
                                            baseStyles.btnDanger,
                                            baseStyles.centerVer
                                        )}
                                        onClick={handleCancel}
                                    >
                                        <span className="material-icons"> close </span>
                                        Cancel
                                    </button>
                                )}
                                {step > 1 && (
                                    <button
                                        type="button"
                                        className={classNames(
                                            baseStyles.btn,
                                            baseStyles.btnPrimary,
                                            baseStyles.btnMd,
                                            baseStyles.centerVer
                                        )}
                                        onClick={handlePrevious}
                                    >
                                        <span className="material-icons"> arrow_back </span>
                                        Previous
                                    </button>
                                )}
                                {step < 4 && (
                                    <button
                                        type="button"
                                        className={classNames(
                                            baseStyles.btn,
                                            baseStyles.btnPrimary,
                                            baseStyles.btnMd,
                                            baseStyles.centerVer
                                        )}
                                        disabled={!isCurrentStepValid(step, values, errors) || isUploadingImage}
                                        onClick={handleNext}
                                    >
                                        {isUploadingImage ? 'Uploading...' : 'Next'}
                                        {!isUploadingImage && <span className="material-icons"> arrow_forward </span>}
                                    </button>
                                )}
                                {step === 4 && (
                                    <button
                                        type="button"
                                        className={classNames(
                                            baseStyles.btn,
                                            baseStyles.btnPrimary,
                                            baseStyles.btnMd,
                                            baseStyles.btnSuccess,
                                            baseStyles.centerVer
                                        )}
                                        onClick={() => handleUpdateEvent(validateForm, setTouched, submitForm)}
                                    >
                                        Update Event
                                    </button>
                                )}
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
}

function BasicDataForm(props: {
    values: FormValues;
    errors: FormikErrors<FormValues>;
    touched: FormikTouched<FormValues>;
    handleChange: FormikHandlers["handleChange"];
    handleBlur: FormikHandlers["handleBlur"];
}) {
    const [endDateVisible, setEndDateVisible] = useState(
        !!(props.values.endDate && props.values.endTime)
    );
    const isFormFieldValid = (name: keyof FormValues) =>
        !!(props.touched[name] && props.errors[name]);

    return (
        <div className="p-fluid grid">
            <div className="col-12">
                <span className="p-float-label">
                    <InputText
                        className={classNames({
                            "p-invalid": isFormFieldValid("displayName"),
                        })}
                        value={props.values.displayName}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        id="displayName"
                        type="text"
                    />
                    <label
                        htmlFor="displayName"
                        className={classNames({
                            "p-error": isFormFieldValid("displayName"),
                        })}
                    >
                        Event Name*
                    </label>
                </span>
            </div>

            <div className="col-6 mt-3">
                <span className="p-float-label">
                    <Calendar
                        className={baseStyles.fullWidth}
                        value={props.values.startDate}
                        onChange={props.handleChange}
                        id="startDate"
                        dateFormat="dd-M-yy"
                    ></Calendar>
                    <label htmlFor="startDate">Start Date</label>
                </span>
            </div>

            <div className="col-6 mt-3">
                <span className="p-float-label">
                    <Calendar
                        className={baseStyles.fullWidth}
                        value={props.values.startTime}
                        onChange={props.handleChange}
                        timeOnly
                        id="startTime"
                        stepMinute={15}
                    ></Calendar>
                    <label htmlFor="startTime">Start Time</label>
                </span>
            </div>
            {endDateVisible && (
                <div className="col-6 mt-3">
                    <span className="p-float-label">
                        <Calendar
                            className={baseStyles.fullWidth}
                            value={props.values.endDate}
                            onChange={props.handleChange}
                            id="endDate"
                            dateFormat="dd-M-yy"
                        ></Calendar>
                        <label htmlFor="startDate">End Date</label>
                    </span>
                </div>
            )}
            {endDateVisible && (
                <div className="col-6 mt-3">
                    <span className="p-float-label">
                        <Calendar
                            className={baseStyles.fullWidth}
                            value={props.values.endTime}
                            onChange={props.handleChange}
                            timeOnly
                            id="endTime"
                            stepMinute={15}
                        ></Calendar>
                        <label htmlFor="startTime">End Time</label>
                    </span>
                </div>
            )}
            <div className="col-12 mt-3">
                {!endDateVisible && (
                    <div
                        className={classNames(baseStyles.clickable, baseStyles.centerVer)}
                        onClick={() => setEndDateVisible(true)}
                    >
                        <span className="material-icons"> add </span>End Date and Time
                    </div>
                )}
                {endDateVisible && (
                    <div
                        className={classNames(baseStyles.clickable, baseStyles.centerVer)}
                        onClick={() => setEndDateVisible(false)}
                    >
                        <span className="material-icons"> remove </span>End Date and Time
                    </div>
                )}
            </div>

            <div className="col-12 mt-3">
                <span className="p-float-label">
                    <InputText
                        className={classNames({
                            "p-invalid": isFormFieldValid("hostName"),
                        })}
                        value={props.values.hostName}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        id="hostName"
                        type="text"
                    />
                    <label
                        htmlFor="hostName"
                        className={classNames({
                            "p-error": isFormFieldValid("hostName"),
                        })}
                    >
                        Host Name*
                    </label>
                </span>
            </div>

            {props.touched.displayName && props.errors.displayName ? (
                <div className={classNames("col-12 mt-3", baseStyles.errorColor)}>
                    {props.errors.displayName}
                </div>
            ) : null}
            {props.touched.hostName && props.errors.hostName ? (
                <div className={classNames("col-12 mt-3", baseStyles.errorColor)}>
                    {props.errors.hostName}
                </div>
            ) : null}
        </div>
    );
}

const LocationForm = (props: {
    setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean | undefined
    ) => void;
    values: FormikValues;
}) => {
    const [shouldFloat, setShouldFloat] = useState(false);

    const setLocationForm = (place: google.maps.places.PlaceResult) => {
        console.log(place);
        props.setFieldValue("location.address", place.formatted_address ?? null);
        props.setFieldValue("location.name", place.name ?? null);
        props.setFieldValue("location.placeId", place.place_id ?? null);
        props.setFieldValue(
            "location.lat",
            place?.geometry?.location?.lat() ?? null
        );
        props.setFieldValue(
            "location.lon",
            place?.geometry?.location?.lng() ?? null
        );
    };

    const buildLocalLocationObject = (e: any) => {
        e.target.value ? setShouldFloat(true) : setShouldFloat(false);
    };

    return (
        <div>
            <h3>Event Location</h3>
            <p>
                Select a Google location to display a map or enter a custom address if
                your location is not listed on Google Maps.
            </p>
            <div className="p-fluid grid">
                <div className="col-12">
                    <EventLocation location={props.values.location} />
                </div>
                <div className="col-12 mt-3">
                    <span className="p-float-label">
                        <Autocomplete
                            className={classNames("p-inputtext", "p-float-label")}
                            placeholder=""
                            id="location-field"
                            apiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY}
                            onChange={(e) => buildLocalLocationObject(e)}
                            onPlaceSelected={(place) => setLocationForm(place)}
                            options={{
                                types: [],
                                fields: ["place_id", "name", "formatted_address", "geometry"],
                            }}
                        />
                        <label
                            htmlFor="location-field"
                            className={
                                shouldFloat ? classNames(styles.floatLabelOverride) : ""
                            }
                        >
                            Location
                        </label>
                    </span>
                </div>
            </div>
        </div>
    );
};

const DescriptionForm = (props: {
    values: FormValues;
    handleChange: FormikHandlers["handleChange"];
    handleBlur: FormikHandlers["handleBlur"];
}) => {
    return (
        <div className="p-fluid grid">
            <div className="col-12 mb-4">
                <b>Description</b>
                <p>
                    <b>bold</b> : **bold** <br />
                    horizontal line : --- (at least 3) <br />
                    hyperlink: start URL with "https://" or "http://" <br />
                </p>
            </div>

            <div className="col-12">
                <span className="p-float-label">
                    <InputTextarea
                        placeholder="Tell your guests what they can expect!"
                        value={props.values.description}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        id="description"
                    />
                    <label htmlFor="description">Description (optional)</label>
                </span>
            </div>
        </div>
    );
};

const ImageForm = (props: {
    values: FormValues;
    handleChange: FormikHandlers["handleChange"];
    handleBlur: FormikHandlers["handleBlur"];
    imageUploadRef: React.RefObject<ImageUploadRef>;
}) => {
    return <div className="p-fluid grid">
        <ImageUpload
            ref={props.imageUploadRef}
            onImageSelect={props.handleChange}
            onImageUploaded={(imageId) => {
                // Update the form field with the image ID
                props.handleChange({
                    target: {
                        name: 'imageId',
                        value: imageId
                    }
                } as any);
            }}
            initialImageId={props.values.imageId}
        />
    </div>;
};

export default EditEvent;

